import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Search, ShieldCheck, ArrowRight, UserCircle2, MessageCircle, Award, BadgeCheck, HeartHandshake } from "lucide-react";
import { trackWhatsApp } from "../lib/beacon";

const SUPPORT_WA = process.env.REACT_APP_SUPPORT_WA || "+5511996420785";
const digits = (s) => (s || "").replace(/[^0-9]/g, "");

const TIER_STYLES = {
  ouro:   { label: "Ouro",   ring: "ring-[#D4A017]", bg: "bg-gradient-to-r from-[#F1C232] to-[#D4A017] text-[#3B2F0B]" },
  prata:  { label: "Prata",  ring: "ring-slate-400", bg: "bg-gradient-to-r from-slate-300 to-slate-500 text-slate-900" },
  bronze: { label: "Bronze", ring: "ring-amber-700", bg: "bg-gradient-to-r from-[#CD7F32] to-[#8B4513] text-white" },
};

/**
 * Vitrine pública. NÃO expõe valor/agenda — só identidade + selo.
 * CTA principal: falar com consultor RAF pelo WhatsApp de plantão. Reserva
 * acontece SOMENTE via consultor, preservando comissão e evitando venda
 * direta.
 */
const PublicProfessionals = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [source, setSource] = useState("todos"); // "todos" | "raf" | "fp"
  const [onCall, setOnCall] = useState(null);

  useEffect(() => {
    api.get("/professionals").then(r => setList(r.data || []))
      .finally(() => setLoading(false));
    api.get("/public/on-call").then(r => setOnCall(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const s = new Set();
    (list || []).forEach(p => (p.categories || []).forEach(c => s.add(c)));
    return Array.from(s).sort();
  }, [list]);

  // Iter 68.66 — "Só Filho Presente" agora bate com a promessa da tab:
  // "Especialistas em cuidado do idoso". Usa `serves_fp_effective` do
  // backend (serves_fp OU fp_professional_id OU categoria geriátrica).
  // Fallback pra `source==="fp"` protege builds intermediários se o campo
  // não vier na resposta.
  const servesFp = (p) => Boolean(p?.serves_fp_effective ?? (p?.source === "fp"));

  const counts = useMemo(() => {
    const c = { todos: list.length, raf: 0, fp: 0 };
    (list || []).forEach(p => {
      if (servesFp(p)) c.fp++;
      else c.raf++;
    });
    return c;
  }, [list]);

  const filtered = useMemo(() => {
    return (list || []).filter(p => {
      if (source === "raf" && servesFp(p)) return false;
      if (source === "fp" && !servesFp(p)) return false;
      if (cat && !(p.categories || []).includes(cat)) return false;
      if (q) {
        const t = `${p.display_name || ""} ${(p.bio || "").slice(0,300)} ${(p.categories||[]).join(" ")}`.toLowerCase();
        if (!t.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [list, q, cat, source]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="public-professionals">
      <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold inline-flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" /> Profissionais credenciados RAF
      </div>
      <h1 className="font-display font-bold text-[#0B2B62] text-3xl md:text-4xl tracking-tight mt-2 text-left">
        Consulta online com <span className="text-emerald-700">profissionais verificados</span>
      </h1>
      <p className="text-slate-600 mt-3 leading-relaxed text-left">
        Psiquiatras, psicólogos, nutricionistas, terapeutas e outros profissionais aprovados pela nossa curadoria. Todos com <b>credencial validada</b> e <b>selo de certificação RAF</b>. Para agendar, fale com o consultor de plantão — ele conhece cada profissional e indica quem melhor se encaixa no seu caso.
      </p>

      {/* Toggle de origem: Todos / Só RAF / Só FP */}
      <div className="mt-6 inline-flex items-center gap-1 border border-slate-200 rounded-full p-1 bg-white"
        role="tablist" aria-label="Filtrar por origem">
        {[
          { v: "todos", l: "Todos", n: counts.todos, hint: "RAF + Filho Presente" },
          { v: "raf",   l: "Só RAF", n: counts.raf, hint: "Cadastros originais da RAF Direct" },
          { v: "fp",    l: "Só Filho Presente", n: counts.fp, hint: "Especialistas em cuidado do idoso" },
        ].map((t) => (
          <button key={t.v} onClick={() => setSource(t.v)}
            data-testid={`prof-source-${t.v}`}
            title={t.hint}
            className={`text-xs px-4 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${
              source === t.v
                ? (t.v === "fp" ? "bg-amber-500 text-white" : "bg-[#0B2B62] text-white")
                : "text-slate-600 hover:bg-slate-50"
            }`}>
            {t.l}
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
              source === t.v ? "bg-white/25" : "bg-slate-100 text-slate-500"
            }`}>{t.n}</span>
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)}
            data-testid="prof-search"
            placeholder="Buscar por nome, especialidade ou palavras da bio"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>
        <select value={cat} onChange={e => setCat(e.target.value)}
          data-testid="prof-category-filter"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
          <option value="">Todas as categorias</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="mt-10 text-slate-500">Carregando profissionais…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 p-8 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-center">
          <UserCircle2 className="w-10 h-10 mx-auto text-slate-400" />
          <p className="mt-3 text-slate-600">
            {source === "fp"
              ? "Nenhum profissional Filho Presente ativo no momento. Alterne pra “Todos” pra ver os outros."
              : source === "raf"
              ? "Nenhum profissional RAF-original nessa categoria. Alterne pra “Todos” pra ver os do Filho Presente."
              : "Ainda não há profissionais certificados nessa categoria. Volte em breve."}
          </p>
        </div>
      ) : (
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="prof-grid">
          {filtered.map(p => <ProfessionalCard key={p.id} p={p} onCall={onCall} />)}
        </div>
      )}
    </div>
  );
};

const ProfessionalCard = ({ p, onCall }) => {
  const tier = (p.certification_tier || "").toLowerCase();
  const style = TIER_STYLES[tier];
  const target = digits(onCall?.online ? (onCall.phone || SUPPORT_WA) : SUPPORT_WA);
  const consultorName = onCall?.online && onCall?.first_name ? onCall.first_name : "consultor RAF";
  const msg = `Olá, ${consultorName}. Vi o perfil da(o) ${p.display_name || "profissional"} na RAF Direct e gostaria de mais informações e agendamento.`;
  const waUrl = `https://wa.me/${target}?text=${encodeURIComponent(msg)}`;

  return (
    <div data-testid={`prof-card-${p.id}`}
      className="bg-white border border-slate-200 rounded-xl p-5 hover:border-emerald-500 hover:shadow-md transition-all flex flex-col relative">
      {(p.serves_fp_effective || p.source === "fp") && (
        <span className="absolute top-3 right-3 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white"
          title="Também atende no Filho Presente — cuidado do idoso.">
          FP
        </span>
      )}
      <Link to={`/profissionais/${p.id}`} className="block group">
        <div className="flex items-start gap-4">
          {p.photo_url ? (
            <img src={p.photo_url} alt="" className={`w-16 h-16 rounded-full object-cover border-2 border-white shadow ${style ? `ring-2 ring-offset-2 ${style.ring}` : ""}`} />
          ) : (
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-slate-400"><UserCircle2 className="w-8 h-8" /></div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <div className="font-display font-bold text-[#0B2B62] text-lg leading-tight truncate group-hover:text-emerald-700">{p.display_name}</div>
              <span
                title="Perfil auditado e aprovado pela curadoria RAF Direct — documentos, conselho de classe e biografia validados."
                data-testid={`prof-verified-${p.id}`}
                className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-600 text-white ring-1 ring-emerald-700 shadow-sm shrink-0">
                <BadgeCheck className="w-3 h-3" strokeWidth={2.5} />
                Verificado
              </span>
            </div>
            <div className="text-xs text-emerald-700 font-semibold mt-0.5 uppercase tracking-widest flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> {p.council_type} · {p.council_state}
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(p.categories || []).slice(0, 3).map(c => (
                <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{c}</span>
              ))}
              {p.accepts_insurance && (
                <span
                  data-testid={`prof-insurance-${p.id}`}
                  title="Este profissional aceita convênios de saúde para teleconsulta na RAF Direct."
                  className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                  <HeartHandshake className="w-3 h-3" /> Aceita convênios
                </span>
              )}
            </div>
          </div>
        </div>
        {p.bio && (
          <p className="text-sm text-slate-600 mt-3 line-clamp-3 text-left">{p.bio}</p>
        )}
      </Link>
      <div className="mt-auto pt-3 flex items-center justify-between gap-2 border-t border-slate-100">
        {style ? (
          <span data-testid={`prof-tier-${p.id}`}
            className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-widest ${style.bg}`}>
            <Award className="w-3 h-3" /> Selo {style.label}
          </span>
        ) : <span />}
        <a href={waUrl} target="_blank" rel="noopener noreferrer"
          onClick={() => trackWhatsApp("public_prof_card", { prof_id: p.id })}
          data-testid={`prof-consult-${p.id}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold">
          <MessageCircle className="w-3.5 h-3.5" /> Falar com consultor
        </a>
      </div>
    </div>
  );
};

export default PublicProfessionals;
